import { Method } from "axios";
import { ActionKeyPair, IService } from "@/shared/hooks/useCustomQuery";
import { EmployeeRole, IUser, IUserById, IUserUpdatePayload, PaginatedResponse, UserRole } from "./types";
import api from "../../../../shared/api/api";
import { uploadFileToSignedUrl } from "@/shared/lib/uploadSignedUrl";

export interface SignedUploadResponse {
  signedUploadUrl: string;
  path: string;
  token: string;
  publicUrl: string;
}

export default class ProfilesService implements IService {
  [key: string]: ActionKeyPair<unknown, unknown>;

  getProfiles: ActionKeyPair<
    {
      search?: string;
      page?: number;
      vip?: boolean;
      subscriber?: boolean;
      isCreator?: boolean;
    },
    PaginatedResponse<IUser>
  > = {
      key: "get-profiles",
      actionBuilder: (params) => async () => {
        return await api<PaginatedResponse<IUser>>({
          url: "/v1/profiles",
          method: "GET" as Method,
          params: {
            ...params,
            vip: params.vip,
            subscriber: params.subscriber,
            isCreator: params.isCreator
          },
        });

      },
    };
  toggleSubscription: ActionKeyPair<Omit<IUser, "id"> & { id: string }, void> = {
    key: "toggle-subscription",
    actionBuilder: (currentUser) => async () => {
      currentUser.subscription = {
        active: !currentUser?.subscription?.active,
        expiresAt:
          currentUser.subscription?.expiresAt ?? new Date().toISOString(),
      };

      return await api({
        url: "/v1/profiles",
        method: "POST" as Method,
        data: currentUser,
      });
    },
  };


  updateUserDate: ActionKeyPair<
    {
      currentUser: IUser;
      expires_at?: string | null;
      vip_expires_at?: string | null;
    },
    void
  > = {
      key: "update-user-date",
      actionBuilder:
        ({ currentUser, expires_at, vip_expires_at }) =>
          async () => {
            // Se tiver vip_expires_at, atualiza via PATCH /profiles/:id
            if (vip_expires_at !== undefined) {
              await api({
                url: `/v1/profiles/${currentUser.id}`,
                method: "PATCH" as Method,
                data: { vipExpiresAt: vip_expires_at },
              });
            }

            // Se tiver subscription, ainda usa a rota antiga ou a rota que for apropriada para subscription
            // (A documentação não menciona subscription no PATCH /profiles/:id)
            if (expires_at) {
              currentUser.subscription = {
                active: Boolean(currentUser.subscription?.active),
                expiresAt: expires_at,
              };
              await api({
                url: "/v1/profiles",
                method: "POST" as Method,
                data: currentUser,
              });
            }
          },
    };

  /** @deprecated Use updateProfileGeneral instead */
  updateUser: ActionKeyPair<Omit<IUser, "id">, void> = {
    key: "update-user-date",
    actionBuilder: (newUserData) => async () => {
      return await api({
        url: "/v1/profiles",
        method: "POST" as Method,
        data: newUserData,
      });
    },
  };

  getProfileById: ActionKeyPair<{ id: string }, IUserById> = {
    key: "get-profile-by-id",
    actionBuilder: ({ id }) => async () => {
      return await api({
        url: `/v1/profiles/${id}`,
        method: "GET" as Method,
      });
    },
  };

  /** Obtém URL assinada para upload do avatar (PATCH /profiles/:id/creator/avatar). */
  getAvatarUploadUrl: ActionKeyPair<{ profileId: string }, SignedUploadResponse> = {
    key: "get-profile-avatar-upload-url",
    actionBuilder: ({ profileId }) => async () => {
      return await api<SignedUploadResponse>({
        url: `/v1/profiles/${profileId}/creator/avatar`,
        method: "PATCH" as Method,
      });
    },
  };

  /** Upload do avatar via URL assinada (PATCH para obter URL + PUT para storage). */
  uploadAvatar: ActionKeyPair<
    { profileId: string; file: File },
    string
  > = {
    key: "upload-profile-avatar",
    actionBuilder: ({ profileId, file }) => async () => {
      const { signedUploadUrl, token, publicUrl } = await api<SignedUploadResponse>({
        url: `/v1/profiles/${profileId}/creator/avatar`,
        method: "PATCH" as Method,
      });
      await uploadFileToSignedUrl(signedUploadUrl, token, file);
      return publicUrl;
    },
  };

  /** Obtém URL assinada para upload do cover (PATCH /profiles/:id/creator/cover). */
  getCoverUploadUrl: ActionKeyPair<{ profileId: string }, SignedUploadResponse> = {
    key: "get-profile-cover-upload-url",
    actionBuilder: ({ profileId }) => async () => {
      return await api<SignedUploadResponse>({
        url: `/v1/profiles/${profileId}/creator/cover`,
        method: "PATCH" as Method,
      });
    },
  };

  /** Upload do cover via URL assinada (PATCH para obter URL + PUT para storage). */
  uploadCover: ActionKeyPair<
    { profileId: string; file: File },
    string
  > = {
    key: "upload-profile-cover",
    actionBuilder: ({ profileId, file }) => async () => {
      const { signedUploadUrl, token, publicUrl } = await api<SignedUploadResponse>({
        url: `/v1/profiles/${profileId}/creator/cover`,
        method: "PATCH" as Method,
      });
      await uploadFileToSignedUrl(signedUploadUrl, token, file);
      return publicUrl;
    },
  };

  updateProfileGeneral: ActionKeyPair<
    { id: string; vip?: boolean; vipExpiresAt?: string | null; username?: string; name?: string },
    IUserById
  > = {
    key: "update-profile-general",
    actionBuilder: ({ id, ...data }) => async () => {
      return await api({
        url: `/v1/profiles/${id}`,
        method: "PATCH" as Method,
        data,
      });
    },
  };

  updateProfileCreator: ActionKeyPair<
    { id: string; role?: UserRole; name?: string; username?: string; isActive?: boolean; dynamicLink?: string; mottoTranslations?: { languageCode: string; motto: string }[] },
    IUserById
  > = {
    key: "update-profile-creator",
    actionBuilder: ({ id, ...data }) => async () => {
      return await api({
        url: `/v1/profiles/${id}/creator`,
        method: "PATCH" as Method,
        data,
      });
    },
  };

  updateProfileEmployee: ActionKeyPair<
    { id: string; employeeRole: EmployeeRole | null },
    IUserById
  > = {
    key: "update-profile-employee",
    actionBuilder: ({ id, employeeRole }) => async () => {
      return await api({
        url: `/v1/profiles/${id}/employee`,
        method: "PATCH" as Method,
        data: { employeeRole },
      });
    },
  };

  /** @deprecated Use updateProfileGeneral, updateProfileCreator, updateProfileEmployee instead */
  createOrUpdateProfile: ActionKeyPair<
    IUserUpdatePayload,
    void
  > = {
      key: "create-or-update-profile",
      actionBuilder: ({ email, data }) => async () => {
        return await api({
          url: `/v1/profiles`,
          method: "POST" as Method,
          params: { email },
          data,
        });
      },
    };

  toggleVip: ActionKeyPair<
    { id: string, vip: boolean, vipExpiresAt: string },
    void
  > = {
      key: "toggle-vip",
      actionBuilder: ({ id, vip, vipExpiresAt }) => async () => {
        return await api({
          url: `/v1/profiles/${id}`,
          method: "PATCH" as Method,
          data: { vip, vipExpiresAt },
        });
      },
    };

  static Instance = new ProfilesService();
}
